import { ApolloError, gql, useMutation } from '@apollo/client';
import { FETCH_IN_PROGRESS_LABELS } from '@modules/taxonomy/hooks/useFetchInProgressLabels';
import { CreateReasonV2Input, Mutation } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

const CREATE_REASON = gql`
  mutation createReasonV2(
    $tenantID: ID!
    $userID: ID
    $displayName: String!
    $description: String
    $metadata: ReasonMetadataInput!
    $records: [mapIDToStringInput!]!
  ) {
    createReasonV2(
      request: {
        tenantID: $tenantID
        userID: $userID
        displayName: $displayName
        description: $description
        metadata: $metadata
        records: $records
      }
    ) {
      id
    }
  }
`;

interface UseCreateReasonOptions {
  onCompleted?: (reasonID: string) => void;
  onError?: (error: ApolloError) => void;
}

export const useCreateReason = ({
  onCompleted,
  onError,
}: UseCreateReasonOptions) => {
  const {
    tenant: { id: tenantID },
    id: userID,
  } = useGetUser();

  const [createReasonMutation, { data, loading }] = useMutation<
    Mutation,
    CreateReasonV2Input
  >(CREATE_REASON, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      onError?.(error);
    },
    onCompleted: (data) => {
      if (data.createReasonV2?.id) {
        onCompleted?.(data.createReasonV2?.id);
      }
    },
    refetchQueries: [FETCH_IN_PROGRESS_LABELS],
  });

  const createReason = useCallback(
    async (input: Omit<CreateReasonV2Input, 'tenantID' | 'userID'>) => {
      return await createReasonMutation({
        variables: {
          ...input,
          tenantID,
          userID,
          metadata: {
            ...input.metadata,
            createdBy: userID,
          },
        },
      });
    },
    [createReasonMutation, tenantID, userID],
  );

  return [createReason, { data, loading }] as const;
};
